import type { CommunitySort } from './communitiesBrowseStore';
import {
  COMMUNITY_BASE_PATH,
  defaultCommunityListFilters,
  FILTER_PRICE_BOUNDS,
  FILTER_SQFT_BOUNDS,
  getLatLng,
  haversineMiles,
  parseListFiltersFromSearchParams,
  writeListFiltersToSearchParams,
  type CommunityListFilters,
  type CommunityProperty,
} from './propertyUtils';

export const SORT_OPTIONS: { value: CommunitySort; label: string }[] = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'distance', label: 'Distance' },
];

export function readUrlState() {
  if (typeof window === 'undefined') {
    return { search: '', filters: defaultCommunityListFilters(), sort: 'featured' as CommunitySort };
  }
  const params = new URLSearchParams(window.location.search);
  const sortRaw = (params.get('sort') || 'featured') as CommunitySort;
  const sort = SORT_OPTIONS.some((option) => option.value === sortRaw) ? sortRaw : 'featured';
  return {
    search: (params.get('search') || '').trim(),
    filters: parseListFiltersFromSearchParams(params),
    sort,
  };
}

export function replaceUrl(
  filters: CommunityListFilters,
  search: string,
  sort: CommunitySort,
  opts: { basePath?: string; hash?: string; extraParams?: Record<string, string> } = {},
) {
  if (typeof window === 'undefined') return;
  const { basePath = COMMUNITY_BASE_PATH, hash = '#communities-results', extraParams = {} } = opts;
  const params = new URLSearchParams();
  writeListFiltersToSearchParams(params, filters, { search, sort });
  Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
  const query = params.toString();
  window.history.replaceState({}, '', `${basePath}${query ? `?${query}` : ''}${hash}`);
}

export function isFilterActive(filters: CommunityListFilters, search: string) {
  return (
    Boolean(search) ||
    filters.priceMin > FILTER_PRICE_BOUNDS.min ||
    filters.priceMax < FILTER_PRICE_BOUNDS.max ||
    filters.sqftMin > FILTER_SQFT_BOUNDS.min ||
    filters.sqftMax < FILTER_SQFT_BOUNDS.max ||
    filters.bedrooms.length > 0 ||
    filters.bathrooms.length > 0 ||
    filters.states.length > 0 ||
    (filters.cities?.length ?? 0) > 0 ||
    (filters.lifestyle?.length ?? 0) > 0
  );
}

export function sortProperties(
  items: CommunityProperty[],
  sort: CommunitySort,
  userLocation: { lat: number; lng: number } | null,
) {
  const copy = [...items];
  if (sort === 'price-asc' || sort === 'price-desc') {
    copy.sort((a, b) => {
      const aPrice = Number(a.property_price_min) || Number.POSITIVE_INFINITY;
      const bPrice = Number(b.property_price_min) || Number.POSITIVE_INFINITY;
      return sort === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;
    });
    return copy;
  }
  if (sort === 'name-asc') {
    copy.sort((a, b) =>
      String(a.property_name || '').localeCompare(String(b.property_name || '')),
    );
    return copy;
  }
  if (sort === 'distance' && userLocation) {
    copy.sort((a, b) => {
      const aCoords = getLatLng(a);
      const bCoords = getLatLng(b);
      const aDist = aCoords ? haversineMiles(userLocation, aCoords) : Number.POSITIVE_INFINITY;
      const bDist = bCoords ? haversineMiles(userLocation, bCoords) : Number.POSITIVE_INFINITY;
      return aDist - bDist;
    });
  }
  return copy;
}
